import { Severity } from '@sentry/react';

import { LDFlags } from '../constants/flags';
import {
  ConsentBasis,
  ConsentHowLinked,
  ConsentIdType,
  ConsentIdentifierInput,
  useConsentOptOutLookupLazyQuery,
  useLinkIdentifiersToOptOutsMutation,
} from '../gql/generated/graphql';
import { logger } from '../services/sentry';
import {
  getBrowserIdentifiers,
  getOptOutCookieBasis,
  markOptedOutForSession,
  resolveVisitorRegion,
  writePrivacyOptOut,
} from '../utils/privacy';
import { queryWithTimeout } from '../utils/queries';
import useFlag from './useFlag';

// Starting value, tune it from the timeout logs
const LOOKUP_TIMEOUT_MS = 1500;

type SubmittedIdentifiers = {
  email?: string | null;
  phone?: string | null;
  auth0_id?: string | null;
};

const getHonoredBases = async (bases: ConsentBasis[]) => {
  if (!bases.includes(ConsentBasis.Gpc) || (await resolveVisitorRegion()).in_scope) {
    return bases;
  }
  return bases.filter((basis) => basis !== ConsentBasis.Gpc);
};

const useOptOutLookup = () => {
  const isLookupEnabled = useFlag(LDFlags.COM_PRIVACY_OPTOUT_LOOKUP);
  const [lookUp] = useConsentOptOutLookupLazyQuery({
    fetchPolicy: 'no-cache',
    context: { isErrorHandled: true },
  });
  const [link] = useLinkIdentifiersToOptOutsMutation({
    context: { isErrorHandled: true },
    ignoreResults: true,
    onError: () => undefined,
  });

  const lookUpOptOuts = async (identifiers: ConsentIdentifierInput[]) => {
    const result = await queryWithTimeout(
      () => lookUp({ variables: { identifiers } }),
      LOOKUP_TIMEOUT_MS,
    );
    // The error link sends network errors to RudderStack only, so log every failure here
    if (result === 'timeout' || result.error) {
      logger.error(
        'useOptOutLookup.ts',
        'Opt-out lookup failed, sending as allowed',
        {},
        result === 'timeout'
          ? new Error(`Failed or timed out after ${LOOKUP_TIMEOUT_MS}ms`)
          : result.error,
        Severity.Warning,
      );
      return [];
    }
    return result.data?.consentOptOutLookup ?? [];
  };

  const linkToOptOuts = (identifiers: ConsentIdentifierInput[], howLinked: ConsentHowLinked) =>
    link({ variables: { identifiers, howLinked } });

  return async ({ email, phone, auth0_id }: SubmittedIdentifiers) => {
    if (!isLookupEnabled) {
      return;
    }

    const submitted = [
      { id_type: ConsentIdType.Email, id_value: email },
      { id_type: ConsentIdType.Phone, id_value: phone },
      { id_type: ConsentIdType.Auth0Id, id_value: auth0_id },
    ].filter((identifier): identifier is ConsentIdentifierInput => !!identifier.id_value?.trim());

    const cookieBasis = getOptOutCookieBasis();
    if (cookieBasis && (await getHonoredBases([cookieBasis])).length) {
      markOptedOutForSession();
      getBrowserIdentifiers().then((browserIdentifiers) =>
        linkToOptOuts(
          [...submitted, ...browserIdentifiers],
          ConsentHowLinked.IdentifiedOnOptedOutBrowser,
        ),
      );
      return;
    }

    const identifiers = [...submitted, ...(await getBrowserIdentifiers())];
    const honoredBases = await getHonoredBases(await lookUpOptOuts(identifiers));
    if (!honoredBases.length) {
      return;
    }

    // A matched gpc opt-out is the person's, not this browser's signal, so the cookie isn't 'gpc'
    if (honoredBases.some((basis) => basis !== ConsentBasis.Glba)) {
      writePrivacyOptOut('button');
    }
    markOptedOutForSession();
    linkToOptOuts(identifiers, ConsentHowLinked.MatchedFromAnotherDevice);
  };
};

export default useOptOutLookup;
